import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, notifyError } from "@/lib/api";
import Dropzone from "@/components/Dropzone";
import {
  FileText, FileArrowUp, MagnifyingGlass, X, Trash, MagicWand, Camera, Buildings,
} from "@phosphor-icons/react";

const DOC_TYPES = [
  { value: "", label: "All types" },
  { value: "invoice", label: "Invoices" },
  { value: "receipt", label: "Receipts" },
  { value: "bank_statement", label: "Statements" },
  { value: "contract", label: "Contracts" },
  { value: "tax_return", label: "Tax returns" },
  { value: "payslip", label: "Payslips" },
  { value: "purchase_order", label: "Purchase orders" },
  { value: "other", label: "Other" },
];

const TYPE_COLOR = {
  invoice: "#FFB020", receipt: "#7AE1B3", bank_statement: "#00A86B",
  contract: "#B487F0", tax_return: "#F08787", payslip: "#87C6F0",
  purchase_order: "#F0C287", other: "#9BA3A0",
};

const fileToB64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(",")[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const readMime = (file) => file?.type || "application/octet-stream";
const readName = (file) => file?.name || "upload";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("");
  const [ocring, setOcring] = useState(false);
  const [modal, setModal] = useState(null); // {parsed, file_base64, file_mime, file_name}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (filterType) params.set("doc_type", filterType);
    const r = await api.get(`/documents?${params.toString()}`);
    setDocs(r.data);
  }, [q, filterType]);
  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    setOcring(true);
    try {
      const b64 = await fileToB64(file);
      const r = await api.post("/documents/ocr", { file_base64: b64 });
      setModal({
        parsed: r.data.data || {},
        file_base64: b64,
        file_mime: readMime(file),
        file_name: readName(file),
      });
    } catch (e) {
      alert("Document OCR failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setOcring(false);
    }
  };

  const save = async () => {
    if (!modal) return;
    setSaving(true);
    try {
      const p = modal.parsed || {};
      await api.post("/documents", {
        doc_type: p.doc_type || "other",
        title: p.title || "Untitled",
        issuer: p.issuer || "",
        recipient: p.recipient || "",
        issue_date: p.issue_date || "",
        total_amount: Number(p.total_amount || 0),
        currency: p.currency || "EUR",
        summary: p.summary || "",
        key_fields: p.key_fields || {},
        entities: p.entities || [],
        notes: p.notes || "",
        file_base64: modal.file_base64,
        file_mime: modal.file_mime,
        file_name: modal.file_name,
      });
      setModal(null);
      load();
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  const viewFile = async (id) => {
    try {
      const r = await api.get(`/documents/${id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank", "noopener");
    } catch (e) { notifyError(e, "No original file stored for this document"); }
  };

  const updateField = (key, val) => setModal((m) => m && ({
    ...m, parsed: { ...(m.parsed || {}), [key]: val },
  }));

  return (
    <div className="space-y-8" data-testid="documents-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Documents</div>
          <h1 className="font-display text-4xl">Every doc, read & filed by AI</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Drop or photograph any invoice, receipt, contract, tax return, payslip or PO —
            AI classifies it, extracts the key fields, and stores it here searchable.
          </p>
        </div>
      </div>

      {/* Dropzone + camera */}
      <Dropzone
        testId="dropzone-document"
        accept="image/*,application/pdf,.pdf,.heic,.heif"
        icon={FileArrowUp}
        label={ocring ? "AI is reading your document…" : "Drop invoice / receipt / statement / any doc here"}
        sublabel="JPG · PNG · HEIC · PDF (multi-page). We auto-classify, extract entities, dates and totals."
        onFile={handleFile}
      />

      <div className="flex gap-3 flex-wrap">
        <label className="btn-ghost cursor-pointer" data-testid="btn-camera-document">
          <Camera size={16} weight="fill" /> Take photo
          <input type="file" accept="image/*" capture="environment" hidden
                 onChange={(e) => handleFile(e.target.files?.[0])} />
        </label>
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={14} />
          <input
            data-testid="input-search-docs"
            placeholder="Search title, issuer, recipient…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8"
          />
        </div>
        <select
          data-testid="select-doc-type"
          value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="max-w-[180px]"
        >
          {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="glass rounded-xl overflow-hidden">
        {docs.length === 0 ? (
          <div className="p-12 text-center text-white/50 text-sm">
            No documents yet. Drop a file above or take a photo.
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Title</th><th>Type</th><th>Issuer</th><th>Date</th><th className="text-right">Amount</th><th></th></tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.document_id} data-testid={`row-doc-${d.document_id}`}>
                  <td>
                    <div className="font-medium">{d.title}</div>
                    {d.summary && <div className="text-white/45 text-[11px] mt-0.5 truncate max-w-md">{d.summary}</div>}
                  </td>
                  <td>
                    <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
                      style={{ background: `${TYPE_COLOR[d.doc_type] || TYPE_COLOR.other}22`, color: TYPE_COLOR[d.doc_type] || TYPE_COLOR.other }}>
                      {(d.doc_type || "other").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="text-white/70 text-xs">{d.issuer || "—"}</td>
                  <td className="text-white/60 mono text-xs">{d.issue_date || "—"}</td>
                  <td className="num">{d.total_amount ? `${d.currency || "EUR"} ${Number(d.total_amount).toFixed(2)}` : "—"}</td>
                  <td className="text-right whitespace-nowrap">
                    {(d.has_file || d.storage_path) && (
                      <button data-testid={`btn-view-doc-file-${d.document_id}`} onClick={() => viewFile(d.document_id)} title="View original file (audit)" className="text-white/40 hover:text-[#FFB020] mr-2">
                        <FileText size={16} />
                      </button>
                    )}
                    <button data-testid={`btn-del-doc-${d.document_id}`} onClick={() => del(d.document_id)} className="text-white/40 hover:text-[#E63946]">
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Review modal */}
      <AnimatePresence>
        {modal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              className="glass rounded-2xl w-full max-w-2xl my-8 p-8" data-testid="doc-review-modal">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="eyebrow mb-1">AI read your document</div>
                  <div className="font-display text-2xl">Review & save</div>
                </div>
                <button onClick={() => setModal(null)} data-testid="btn-close-doc-modal" className="text-white/60 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="rounded-xl border border-dashed border-white/15 bg-black/20 p-4 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#FFB02022" }}>
                  <MagicWand size={18} weight="fill" color="#FFB020" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{modal.file_name}</div>
                  <div className="text-[11px] text-white/45">{modal.file_mime}</div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
                  style={{ background: `${TYPE_COLOR[modal.parsed?.doc_type] || TYPE_COLOR.other}22`, color: TYPE_COLOR[modal.parsed?.doc_type] || TYPE_COLOR.other }}>
                  {(modal.parsed?.doc_type || "other").replace(/_/g, " ")}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label>Title</label>
                  <input data-testid="input-doc-title" value={modal.parsed?.title || ""} onChange={(e) => updateField("title", e.target.value)} />
                </div>
                <div><label>Type</label>
                  <select data-testid="select-doc-type-modal" value={modal.parsed?.doc_type || "other"} onChange={(e) => updateField("doc_type", e.target.value)}>
                    {DOC_TYPES.slice(1).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div><label>Issuer</label>
                  <input data-testid="input-doc-issuer" value={modal.parsed?.issuer || ""} onChange={(e) => updateField("issuer", e.target.value)} placeholder="Who issued this doc"/>
                </div>
                <div><label>Recipient</label>
                  <input data-testid="input-doc-recipient" value={modal.parsed?.recipient || ""} onChange={(e) => updateField("recipient", e.target.value)} placeholder="Who was it for"/>
                </div>
                <div><label>Issue date</label>
                  <input data-testid="input-doc-date" type="date" value={modal.parsed?.issue_date || ""} onChange={(e) => updateField("issue_date", e.target.value)} />
                </div>
                <div><label>Total (€)</label>
                  <input data-testid="input-doc-amount" type="number" step="0.01" value={modal.parsed?.total_amount || 0} onChange={(e) => updateField("total_amount", Number(e.target.value))} />
                </div>
                <div className="sm:col-span-2">
                  <label>Summary</label>
                  <textarea data-testid="input-doc-summary" rows={2} value={modal.parsed?.summary || ""} onChange={(e) => updateField("summary", e.target.value)} />
                </div>
                {modal.parsed?.entities?.length > 0 && (
                  <div className="sm:col-span-2">
                    <label>Entities extracted</label>
                    <div className="flex flex-wrap gap-2">
                      {modal.parsed.entities.map((e, i) => (
                        <span key={`${e.name}-${i}`} className="px-2 py-1 rounded-full text-[11px] bg-white/5 border border-white/10 inline-flex items-center gap-1">
                          <Buildings size={11} /> {e.name}{e.role ? ` · ${e.role}` : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-white/5">
                <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="btn-save-doc" className="btn-gold">
                  <FileText size={16} weight="fill" /> {saving ? "Saving…" : "Save document"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
